import {
  DOCUMENT_ACCEPT,
  DOCUMENT_MAX_BYTES,
  DOCUMENT_MAX_PER_REQUEST,
  type DocumentProblem,
  type DocumentView,
  type RequestPanel,
} from "@procuremate/shared";
import { useMutation } from "@tanstack/react-query";
import { ApiRequestError, api } from "@/lib/api-client";
import { useApplyPanel } from "./api";

/** Checks the browser can make before sending (the server decides again from the bytes). */
export function precheck(file: File, attached: number): DocumentProblem | null {
  if (file.size === 0) return "empty";
  if (file.size > DOCUMENT_MAX_BYTES) return "too_large";
  if (attached >= DOCUMENT_MAX_PER_REQUEST) return "too_many";
  const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
  if (!DOCUMENT_ACCEPT.split(",").includes(extension)) return "unsupported_type";
  return null;
}

/**
 * POST /api/requests/:id/documents with upload progress (fetch cannot report it, XHR can).
 * Same-origin with the session cookie, like `api()`; errors become ApiRequestError.
 */
export function uploadDocument(
  requestId: string,
  file: File,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<DocumentView> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/requests/${requestId}/documents`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as DocumentView);
        return;
      }
      const error = (body as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
      // 413: above the parser's hard cap — the same thing for the requester as "too large".
      const details = xhr.status === 413 ? { reason: "too_large" } : error?.details;
      reject(new ApiRequestError(xhr.status, error?.code ?? "INTERNAL", error?.message ?? "Upload failed", details));
    };
    xhr.onerror = () => reject(new TypeError("Network error"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}

/** DELETE /api/requests/:id/documents/:documentId — returns the refreshed panel. */
export function useRemoveDocument(requestId: string) {
  const applyPanel = useApplyPanel(requestId);
  return useMutation({
    mutationFn: (documentId: string) =>
      api<RequestPanel>(`/requests/${requestId}/documents/${documentId}`, { method: "DELETE" }),
    onSuccess: applyPanel,
  });
}

/** Why an upload failed, as a key of requests.documents.problems. */
export function uploadProblem(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const reason = (error.details as { reason?: string } | undefined)?.reason;
    if (reason) return reason;
    if (error.status === 409) return "closed";
    if (error.status === 403) return "forbidden";
  }
  return "upload_failed";
}

export const documentDownloadUrl = (requestId: string, documentId: string) =>
  `/api/requests/${requestId}/documents/${documentId}/content`;
